import React from "react";
import { Helmet } from "react-helmet-async";
import {
    Box, Button, Card, CardContent, Chip, Container,
    Divider, Stack, Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";

const githubUrl = "https://github.com/HashedGrey/Sparrow-x";
const pageUrl = "https://aggreylelei.com/projects/";
const architectureUrl = "https://aggreylelei.com/blog/agentic-architecture";
const sparrowxVideo = "/sparrowx-flow.mp4";
const socialImage = "https://aggreylelei.com/og-embabel.jpg";
const pageTitle = "SparrowX | Security & Internal Intelligence";
const pageDescription =
    "An agentic internal intelligence system connecting company documents and service ownership to support security investigations, operational risk analysis, and cited decisions.";

const tags = [
    "Security Investigation", "Internal Intelligence", "Operational Risk",
    "Agentic Orchestration", "Hybrid Retrieval", "Cited Evidence",
    "Service Ownership", "Embabel", "Spring Boot", "gRPC", "Langfuse",
];

const serviceCards = [
    {
        icon: <PsychologyRoundedIcon />,
        title: "agenticsvc",
        eyebrow: "Mission Orchestration & Synthesis",
        body: "Interprets missions, plans internal lookups and document searches, correlates results, and produces cited findings with responsible owners and questions requiring verification.",
    },
    {
        icon: <HubRoundedIcon />,
        title: "intsvc",
        eyebrow: "Ownership & Service Context",
        body: "Resolves services, teams, responsible engineers, repositories, runbook records, service metadata, and available relationships across internal domain data.",
    },
    {
        icon: <DescriptionRoundedIcon />,
        title: "docsvc",
        eyebrow: "Document Retrieval & Evidence",
        body: "Extracts and searches company documents using hybrid retrieval. Returns relevant passages from incident reports, policies, architecture documents, deployment procedures, and runbooks for citation and synthesis.",
    },
    {
        icon: <AccountTreeRoundedIcon />,
        title: "bb",
        eyebrow: "Shared Foundation Library",
        body: "Provides shared command and query handling, validation, context propagation, observability, exception handling, and domain primitives across the executable services.",
    },
];

const capabilityCards = [
    {
        icon: <SecurityRoundedIcon />,
        title: "Internal Security Investigation",
        query: "An incident report describes suspected credential exposure involving the Agentic Orchestrator service. Identify the responsible team, find the applicable credential-handling policies and incident-response runbooks, and outline the investigation and remediation steps supported by those documents.",
        execution: [
            {
                service: "intsvc",
                detail: "Resolves the affected service, owning team, responsible engineers, and available service relationships.",
            },
            {
                service: "docsvc",
                detail: "Retrieves the incident report, credential-handling policies, relevant service documentation, and incident-response runbooks.",
            },
            {
                service: "agenticsvc",
                detail: "Correlates the reported incident with ownership and security guidance, then produces a cited investigation plan separating documented facts from questions requiring verification.",
            },
        ],
        outcome: "Investigation brief with accountable owners, supporting evidence, and documented response steps.",
    },
    {
        icon: <SearchRoundedIcon />,
        title: "Internal Knowledge Discovery",
        query: "For the Agentic Orchestrator service, identify the owning team and primary engineers, find its architecture documents and deployment procedures, and locate the runbooks relevant to increased latency during agent execution.",
        execution: [
            {
                service: "intsvc",
                detail: "Resolves the service, owning team, associated engineers, and service metadata.",
            },
            {
                service: "docsvc",
                detail: "Retrieves relevant architecture documents, deployment procedures, and operational runbooks.",
            },
            {
                service: "agenticsvc",
                detail: "Connects ownership and document evidence into a cited answer explaining responsibility, service operation, and the guidance applicable to the reported symptom.",
            },
        ],
        outcome: "Service intelligence brief connecting technical context, operational guidance, and ownership.",
    },
    {
        icon: <AnalyticsRoundedIcon />,
        title: "Operational Risk & Process Analysis",
        query: "Assess the Agentic Orchestrator service’s readiness for production deployments. Compare its documented procedures with company deployment standards, examine incident reports for recurring model timeouts, and identify gaps in rollback guidance, operational ownership, and runbook coverage.",
        execution: [
            {
                service: "intsvc",
                detail: "Gathers service metadata, ownership, team assignments, and available dependency information.",
            },
            {
                service: "docsvc",
                detail: "Retrieves company deployment standards, service procedures, architecture documents, incident reports, and runbooks.",
            },
            {
                service: "agenticsvc",
                detail: "Compares documented practices against requirements, identifies recurring reported issues and evidence gaps, and proposes follow-up actions with responsible owners.",
            },
        ],
        outcome: "Cited assessment of documented readiness, coverage gaps, and actions requiring owner review.",
    },
];

const executionSteps = [
    {
        title: "1. Scope the Mission",
        body: "Identify the incident, service, or operational question, then determine which internal records and document evidence are needed.",
    },
    {
        title: "2. Resolve Internal Context",
        body: "Resolve service identity, ownership, engineers, and available relationships. Use that context to focus subsequent document searches.",
    },
    {
        title: "3. Retrieve & Correlate Evidence",
        body: "Retrieve relevant report passages, policies, procedures, and runbooks. Follow references into further lookups when the mission requires additional context.",
    },
    {
        title: "4. Produce a Cited Assessment",
        body: "Connect findings to source passages, distinguish reported facts from inferences, and identify unresolved questions, recommended actions, and responsible owners.",
    },
];

const assuranceCards = [
    {
        icon: <DescriptionRoundedIcon />,
        title: "Traceable Evidence",
        body: "Citations connect findings to retrieved passages so reviewers can inspect the source, context, and basis for each recommendation.",
    },
    {
        icon: <HubRoundedIcon />,
        title: "Accountable Ownership",
        body: "Service and team records establish who owns the affected system and which engineers can validate findings or coordinate follow-up.",
    },
    {
        icon: <VisibilityRoundedIcon />,
        title: "Auditable Agent Execution",
        body: "Langfuse traces make orchestration, internal lookups, document retrieval, and model calls available for execution review and troubleshooting.",
    },
    {
        icon: <AnalyticsRoundedIcon />,
        title: "Explicit Evidence Gaps",
        body: "Assessments identify missing or conflicting evidence and distinguish gaps in retrieved documentation from confirmed operational deficiencies.",
    },
];

const panelSx: SxProps<Theme> = {
    color: "#f5f7ff",
    borderRadius: 5,
    background:
        "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.025))",
    border: "1px solid rgba(255,255,255,0.1)",
};

const bodySx: SxProps<Theme> = {
    color: "rgba(245,247,255,0.78)",
    lineHeight: 1.85,
};

const gridSx: SxProps<Theme> = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
    gap: 3,
};

const buttonSx: SxProps<Theme> = {
    px: 3,
    py: 1.3,
    borderRadius: 999,
    fontWeight: 700,
    textTransform: "none",
    bgcolor: "#7c3aed",
    "&:hover": { bgcolor: "#6d28d9" },
};

const outlineButtonSx: SxProps<Theme> = {
    px: 3,
    py: 1.3,
    borderRadius: 999,
    fontWeight: 700,
    textTransform: "none",
    color: "#efeaff",
    borderColor: "rgba(239,234,255,0.35)",
};

function Section({
                     title,
                     children,
                 }: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <Box component="section" aria-label={title}>
            <Typography
                component="h2"
                sx={{
                    fontSize: { xs: "1.6rem", md: "2rem" },
                    fontWeight: 800,
                    mb: 3,
                }}
            >
                {title}
            </Typography>
            {children}
        </Box>
    );
}

function CardHeading({
                         icon,
                         title,
                         eyebrow,
                     }: {
    icon: React.ReactNode;
    title: string;
    eyebrow?: string;
}) {
    return (
        <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2 }}
        >
            <Box
                sx={{
                    width: 46,
                    height: 46,
                    flexShrink: 0,
                    borderRadius: 3,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(139,92,246,0.16)",
                    color: "#d8c8ff",
                }}
            >
                {icon}
            </Box>

            <Box>
                <Typography
                    component="h3"
                    sx={{ fontWeight: 800, fontSize: "1.1rem" }}
                >
                    {title}
                </Typography>

                {eyebrow && (
                    <Typography
                        sx={{ color: "#c4b5fd", fontSize: "0.88rem" }}
                    >
                        {eyebrow}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

const ProjectsPage: React.FC = () => (
    <>
        <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            <meta name="author" content="Aggrey Lelei" />
            <meta
                name="robots"
                content="index, follow, max-image-preview:large"
            />
            <link rel="canonical" href={pageUrl} />

            <meta property="og:site_name" content="Aggrey Lelei" />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:image" content={socialImage} />
            <meta property="og:image:type" content="image/jpeg" />
            <meta
                property="og:image:alt"
                content="SparrowX agentic orchestration visualization"
            />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            <meta name="twitter:image" content={socialImage} />
            <meta
                name="twitter:image:alt"
                content="SparrowX agentic orchestration visualization"
            />
            <meta name="twitter:site" content="@aggreykiplimo" />
            <meta name="twitter:creator" content="@aggreykiplimo" />

            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "SoftwareSourceCode",
                    name: "SparrowX",
                    url: pageUrl,
                    codeRepository: githubUrl,
                    description: pageDescription,
                    author: {
                        "@type": "Person",
                        name: "Aggrey Lelei",
                    },
                    programmingLanguage: ["Java", "TypeScript"],
                    keywords: tags.join(", "),
                })}
            </script>
        </Helmet>

        <Box
            component="main"
            sx={{
                minHeight: "100vh",
                background:
                    "radial-gradient(circle at top, rgba(123,97,255,0.12), transparent 28%), linear-gradient(180deg, #0b0b14 0%, #111120 100%)",
                color: "#f5f7ff",
                py: { xs: 6, md: 10 },
            }}
        >
            <Container maxWidth="lg">
                <Stack spacing={8}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1.1fr 0.9fr",
                            },
                            gap: { xs: 4, md: 6 },
                            alignItems: "center",
                        }}
                    >
                        <Box>
                            <Chip
                                icon={<SecurityRoundedIcon />}
                                label="Security & Internal Intelligence"
                                sx={{
                                    mb: 2.5,
                                    color: "#efeaff",
                                    bgcolor: "rgba(138,99,255,0.18)",
                                    "& .MuiChip-icon": {
                                        color: "#c4b5fd",
                                    },
                                }}
                            />

                            <Typography
                                component="h1"
                                sx={{
                                    fontSize: {
                                        xs: "2.6rem",
                                        md: "4.2rem",
                                    },
                                    fontWeight: 800,
                                    lineHeight: 1.05,
                                    letterSpacing: "-0.03em",
                                    mb: 2.5,
                                }}
                            >
                                SparrowX
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: "1.1rem",
                                        md: "1.25rem",
                                    },
                                    lineHeight: 1.8,
                                    color: "#e8ddff",
                                }}
                            >
                                Agentic internal intelligence for security
                                investigations, service ownership, and
                                operational risk.
                            </Typography>

                            <Typography sx={{ ...bodySx, mt: 2.5 }}>
                                Connect company documents with structured
                                internal data to identify accountable owners,
                                retrieve applicable guidance, and produce cited
                                assessments with auditable agent traces.
                            </Typography>

                            <Typography sx={{ ...bodySx, mt: 2.5 }}>
                                Built for security engineers, incident leads,
                                platform engineers, and technical decision-makers
                                assessing complex internal systems.
                            </Typography>

                            <Stack
                                direction="row"
                                spacing={2}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{ mt: 4 }}
                            >
                                <Button
                                    href={githubUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="contained"
                                    endIcon={<OpenInNewRoundedIcon />}
                                    sx={buttonSx}
                                >
                                    View on GitHub
                                </Button>

                                <Button
                                    href={architectureUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="outlined"
                                    sx={outlineButtonSx}
                                >
                                    Architecture Rationale
                                </Button>
                            </Stack>

                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{ mt: 4 }}
                            >
                                {tags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        sx={{
                                            color: "#e8ddff",
                                            bgcolor:
                                                "rgba(255,255,255,0.06)",
                                            border:
                                                "1px solid rgba(255,255,255,0.1)",
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: 6,
                                overflow: "hidden",
                                background:
                                    "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
                                border: "1px solid rgba(255,255,255,0.08)",
                                boxShadow: "0 20px 60px rgba(0,0,0,0.32)",
                                //backdropFilter: "blur(16px)",
                            }}
                        >
                            <Box
                                component="a"
                                href={sparrowxVideo}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                <Box
                                    component="video"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain", // ← important for clarity
                                        display: "block",
                                    }}
                                >
                                    <source src={sparrowxVideo} type="video/mp4" />
                                </Box>
                            </Box>
                            <CardContent sx={{ p: 3.2 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 1 }}>
                                    Agentic Internal Knowledge Flow
                                </Typography>
                                <Typography
                                    sx={{
                                        color: "rgba(245,247,255,0.76)",
                                        lineHeight: 1.8,
                                        fontSize: "0.98rem",
                                    }}
                                >
                                    Visualizes how a user mission moves through agenticsvc, intsvc,
                                    docsvc, and the shared building-block foundation to produce grounded,
                                    cited engineering answers.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Divider
                        sx={{ borderColor: "rgba(255,255,255,0.1)" }}
                    />

                    <Section title="What SparrowX Can Do">
                        <Stack spacing={3}>
                            {capabilityCards.map((item) => (
                                <Card
                                    key={item.title}
                                    elevation={0}
                                    sx={panelSx}
                                >
                                    <CardContent
                                        sx={{ p: { xs: 3, md: 4 } }}
                                    >
                                        <CardHeading
                                            icon={item.icon}
                                            title={item.title}
                                        />

                                        <Typography
                                            component="blockquote"
                                            sx={{
                                                m: 0,
                                                mb: 3,
                                                p: 2.5,
                                                borderLeft:
                                                    "3px solid #a78bfa",
                                                bgcolor:
                                                    "rgba(0,0,0,0.18)",
                                                borderRadius: 2,
                                                color: "#e8ddff",
                                                lineHeight: 1.85,
                                            }}
                                        >
                                            “{item.query}”
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                mb: 1.5,
                                            }}
                                        >
                                            Expected execution
                                        </Typography>

                                        <Stack
                                            component="ul"
                                            spacing={1.5}
                                            sx={{ m: 0, pl: 2.5 }}
                                        >
                                            {item.execution.map((step) => (
                                                <Box
                                                    component="li"
                                                    key={step.service}
                                                >
                                                    <Typography sx={bodySx}>
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                color: "#d8c8ff",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {step.service}
                                                        </Box>
                                                        {" — "}
                                                        {step.detail}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>

                                        <Divider
                                            sx={{
                                                my: 2.5,
                                                borderColor:
                                                    "rgba(255,255,255,0.1)",
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                ...bodySx,
                                                color: "#e8ddff",
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{ fontWeight: 700 }}
                                            >
                                                Decision support:{" "}
                                            </Box>
                                            {item.outcome}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </Section>

                    <Section title="Core Services">
                        <Typography sx={{ ...bodySx, mb: 3 }}>
                            Three executable services separate mission
                            orchestration, structured internal context, and
                            document intelligence. A shared foundation library
                            supports consistent service behavior.
                        </Typography>

                        <Box sx={gridSx}>
                            {serviceCards.map((item) => (
                                <Card
                                    key={item.title}
                                    elevation={0}
                                    sx={panelSx}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <CardHeading
                                            icon={item.icon}
                                            title={item.title}
                                            eyebrow={item.eyebrow}
                                        />
                                        <Typography sx={bodySx}>
                                            {item.body}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Section>

                    <Section title="Mission Lifecycle">
                        <Box sx={gridSx}>
                            {executionSteps.map((step) => (
                                <Card
                                    key={step.title}
                                    elevation={0}
                                    sx={panelSx}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography
                                            component="h3"
                                            sx={{
                                                fontWeight: 800,
                                                mb: 1.5,
                                            }}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Typography sx={bodySx}>
                                            {step.body}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Section>

                    <Section title="Evidence, Ownership & Execution Review">
                        <Box sx={gridSx}>
                            {assuranceCards.map((item) => (
                                <Card
                                    key={item.title}
                                    elevation={0}
                                    sx={panelSx}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <CardHeading
                                            icon={item.icon}
                                            title={item.title}
                                        />
                                        <Typography sx={bodySx}>
                                            {item.body}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Section>

                    <Section title="SparrowX as an Investigation Subject">
                        <Card elevation={0} sx={panelSx}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Typography sx={{ ...bodySx, mb: 2 }}>
                                    SparrowX can apply the same workflow to its
                                    own indexed architecture documents, service
                                    metadata, ownership records, and runbooks.
                                </Typography>

                                <Typography
                                    component="blockquote"
                                    sx={{
                                        m: 0,
                                        p: 2.5,
                                        borderLeft: "3px solid #a78bfa",
                                        bgcolor: "rgba(0,0,0,0.18)",
                                        lineHeight: 1.85,
                                        color: "#e8ddff",
                                    }}
                                >
                                    “Review the documented production readiness
                                    of SparrowX’s Agentic Orchestrator. Identify
                                    its owners and documented dependencies,
                                    compare deployment and rollback procedures
                                    with company standards, and cite any gaps
                                    requiring owner verification.”
                                </Typography>

                                <Typography sx={{ ...bodySx, mt: 2 }}>
                                    The resulting assessment connects the
                                    system’s documented design with operational
                                    accountability and the evidence available
                                    for review.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Section>

                    <Box
                        component="section"
                        sx={{
                            ...panelSx,
                            p: { xs: 3, md: 4.5 },
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            component="h2"
                            sx={{
                                fontSize: {
                                    xs: "1.5rem",
                                    md: "2rem",
                                },
                                fontWeight: 800,
                                mb: 2,
                            }}
                        >
                            Investigate with Context. Decide with Evidence.
                        </Typography>

                        <Typography
                            sx={{
                                ...bodySx,
                                maxWidth: 820,
                                mx: "auto",
                                mb: 3,
                            }}
                        >
                            SparrowX brings service ownership, company policies,
                            incident reports, and operational guidance into a
                            cited assessment that senior technical teams can
                            inspect and act on.
                        </Typography>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            justifyContent="center"
                        >
                            <Button
                                href={githubUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                endIcon={<OpenInNewRoundedIcon />}
                                sx={buttonSx}
                            >
                                Explore the Repository
                            </Button>

                            <Button
                                href="/blog"
                                variant="outlined"
                                sx={outlineButtonSx}
                            >
                                Read Technical Articles
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
            </Container>
        </Box>
    </>
);

export default ProjectsPage;

